/**
 * Widget API Service
 * ===================
 * Fetches data for all dashboard widgets from backend APIs.
 *
 * Migrated to use central `apiClient` (cookie auth + CSRF + JSON parsing
 * + 401 redirect handled in one place). Each fetch returns the parsed
 * JSON body directly or `null` on failure — preserves the prior
 * contract callers depend on.
 */
import { apiClient } from '@/lib/apiClient';

const safe = async (label, promise) => {
  try {
    return await promise;
  } catch (error) {
    // Widgets gracefully fall back to demo data on failure (e.g. 403 when the
    // demo tenant has no live source) — keep this off the console/network noise.
    // eslint-disable-next-line no-console
    console.debug(`${label} API unavailable, using demo data:`, error?.message || error);
    return null;
  }
};

export const fetchRevenueForecast = (days = 30, scenario = 'baseline', industry = 'hospitality') =>
  safe('Revenue forecast', apiClient.get(`/api/widgets/revenue-forecast?days=${days}&scenario=${scenario}&industry=${industry}`));

export const fetchPLSnapshot = (industry = 'hospitality') =>
  safe('P&L snapshot', apiClient.get(`/api/widgets/pl-snapshot?industry=${industry}`));

export const fetchStaffPerformance = () =>
  safe('Staff performance', apiClient.get('/api/widgets/staff-performance'));

export const fetchCustomerSentiment = () =>
  safe('Customer sentiment', apiClient.get('/api/widgets/customer-sentiment'));

export const fetchCompetitorIntelligence = (industry = 'hospitality') =>
  safe('Competitor intelligence', apiClient.get(`/api/widgets/competitor-intelligence?industry=${industry}`));

export const fetchSupplierPerformance = () =>
  safe('Supplier performance', apiClient.get('/api/widgets/supplier-performance'));

export const fetchWeatherImpact = () =>
  safe('Weather impact', apiClient.get('/api/widgets/weather-impact'));

export const fetchGoals = () =>
  safe('Goals', apiClient.get('/api/widgets/goals'));

export const fetchAllWidgetData = async (industry = 'hospitality') => {
  const results = await Promise.allSettled([
    fetchRevenueForecast(),
    fetchPLSnapshot(industry),
    fetchStaffPerformance(),
    fetchCustomerSentiment(),
    fetchCompetitorIntelligence(industry),
    fetchSupplierPerformance(),
    fetchWeatherImpact(),
    fetchGoals(),
  ]);
  return {
    revenueForecast: results[0].status === 'fulfilled' ? results[0].value : null,
    plSnapshot: results[1].status === 'fulfilled' ? results[1].value : null,
    staffPerformance: results[2].status === 'fulfilled' ? results[2].value : null,
    customerSentiment: results[3].status === 'fulfilled' ? results[3].value : null,
    competitorIntelligence: results[4].status === 'fulfilled' ? results[4].value : null,
    supplierPerformance: results[5].status === 'fulfilled' ? results[5].value : null,
    weatherImpact: results[6].status === 'fulfilled' ? results[6].value : null,
    goals: results[7].status === 'fulfilled' ? results[7].value : null,
  };
};

export default {
  fetchRevenueForecast,
  fetchPLSnapshot,
  fetchStaffPerformance,
  fetchCustomerSentiment,
  fetchCompetitorIntelligence,
  fetchSupplierPerformance,
  fetchWeatherImpact,
  fetchGoals,
  fetchAllWidgetData,
};
